import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input, Spin } from 'antd';
import { UtilService } from '../../../../core/services/util.service';
import { AuthService } from '../../services/auth-login.service';
import { AccessTokenPayload } from '../../../../core/models/auth/access-token-payload.model';
import { AccessToken } from '../../../../core/models/auth/access-token.model';
import { Session } from '../../../../core/models/auth/session.model';
import { useAuthStore } from '../../../../core/store/auth.store';
import { useRouteStore } from '../../../../core/store/route.store';
import { PreloadService } from '../../../../core/services/preload.service';
import { ProfileService } from '../../../private/modules/profile/service/profile.service';
import { route } from '../../../../constants/routes';
import { User } from '../../models/user.model';
import SiteBrand from '../../../../core/components/SiteBrand';

interface OtpVerificationProps {
  username: string;
  password: string;
}

const OtpVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuthStore();
  const { setRoutePrefix } = useRouteStore();
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    // Get credentials from location state
    const state = location.state as OtpVerificationProps;
    if (state?.username && state?.password) {
      setUsername(state.username);
      setPassword(state.password);
    } else {
      // If no credentials, redirect back to login
      navigate('/login');
    }
  }, [location.state, navigate]);

  const HandleOtpInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(event.target.value);
  };

  const VerifyOtp = async () => {
    if (!otp.trim()) {
      UtilService.Alert('Important!', 'info', 'Please enter your OTP code.');
      return;
    }

    try {
      setLoading(true);
      
      // Create login request with OTP
      const loginUser = new User(username, '', password, otp);

      const item: AccessToken = await AuthService.Login(loginUser);

      if (item && item.accessToken) {
        localStorage.setItem('token', item.accessToken);

        const payload: AccessTokenPayload = UtilService.DecodeJWT(item.accessToken);

        const session: Session = {
          session_state: payload.session_state,
          realm_access: payload.realm_access,
          resource_access: payload.resource_access,
          scope: payload.scope,
          sid: payload.sid,
          email_verified: payload.email_verified,
          name: payload.name,
          preferred_username: payload.preferred_username,
          given_name: payload.given_name,
          family_name: payload.family_name,
          email: payload.email,
        };

        setAuth(item, payload, session);
        
        try {
          const userProfile = await ProfileService.Get();
          if (userProfile?.id && userProfile?.companyId) {
            localStorage.setItem('userProfile', JSON.stringify(userProfile));
            // Set the route prefix based on company name
            setRoutePrefix(userProfile.companyName);
            await PreloadService.preloadAssets(
              userProfile.id,
              userProfile.companyId,
              userProfile.userFile?.file?.id
            );
            sessionStorage.setItem('useDashboardPreloadJobs', 'true');
            sessionStorage.setItem('useDashboardPreloadJobApps', 'true');
          }
        } catch (preloadError) {
          console.warn('Failed to preload assets:', preloadError);
        }
        
        navigate(route('/dashboard'));
      } else {
        console.error('Login failed: No token received');
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);
      
      if (error.message && error.message.includes('INVALID_OTP')) {
        UtilService.Alert('Invalid OTP', 'error', 'The OTP code you entered is invalid. Please try again.');
        setOtp(''); // Clear the OTP field
      } else {
        UtilService.Alert('Error', 'error', 'OTP verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const GoBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen flex-col justify-center px-6 py-12 lg:px-8">
      <div className="max-w-[320px] w-full m-auto bg-white p-8 rounded-lg shadow-[0_0_0_1px_rgba(0,0,0,0.1),0_4px_12px_rgba(0,0,0,0.15)]">
        
        <div className="sm:mx-auto w-full">
          <div className="mx-auto flex justify-center">
            <SiteBrand markSize={48} layout="stacked" />
          </div>
          <h2 className="mt-4 text-center text-2xl font-bold tracking-tight text-gray-900">
            Enter OTP Code 
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please enter the 6-digit code from your authenticator app
          </p>
        </div>

        <div className="mt-8 w-full space-y-6">
          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
              OTP Code
            </label>
            <Input
              id="otp"
              name="otp"
              type="text"
              placeholder="123456"
              value={otp}
              onChange={HandleOtpInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  VerifyOtp();
                }
              }}
              maxLength={6}
              className="text-center text-lg tracking-widest"
            />
          </div>

          <button
            onClick={VerifyOtp}
            disabled={loading || !otp.trim()}
            className="w-full rounded-md px-3 py-1.5 text-sm font-semibold shadow-sm mt-2 text-white ac-btn ac-btn-primary"
          >
            {loading ? (
              <Spin />
            ) : (
              'Verify OTP'
            )}
          </button>

          <button
            onClick={GoBackToLogin}
            disabled={loading}
   className="w-full rounded-md bg-blue-700 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-800"
 >
            Back to Login
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Having trouble? Make sure your device time is synchronized.
          </p>
        </div>

      </div>
    </div>
  );
};

export default OtpVerification;
